export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  OPERATOR: 'operator',
  CLIENT: 'client'
};

export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: {
    name: 'Super Admin',
    level: 4,
    capabilities: [
      'manage_all_users',
      'manage_all_organizations',
      'manage_all_brands',
      'view_all_analytics',
      'system_administration',
      'manage_agents',
      'manage_admins',
      'manage_clients',
      'assign_organizations',
      'assign_brands',
      'create_organizations',
      'create_brands',
      'delete_organizations',
      'delete_brands'
    ],
    restrictions: []
  },
  [USER_ROLES.ADMIN]: {
    name: 'Admin',
    level: 3,
    capabilities: [
      'manage_clients',
      'manage_organizations',
      'manage_brands',
      'view_analytics',
      'assign_organizations',
      'assign_brands',
      'create_organizations',
      'create_brands',
      'manage_agents'
    ],
    restrictions: [
      'cannot_manage_super_admins',
      'cannot_modify_system_settings'
    ]
  },
  [USER_ROLES.OPERATOR]: {
    name: 'Operator',
    level: 2,
    capabilities: [
      'manage_assigned_clients',
      'view_assigned_analytics',
      'create_brands_for_clients',
      'manage_client_organizations',
      'assist_clients',
      'generate_reports'
    ],
    restrictions: [
      'cannot_create_organizations',
      'cannot_manage_other_agents',
      'limited_to_assigned_clients'
    ]
  },
  [USER_ROLES.CLIENT]: {
    name: 'Client',
    level: 1,
    capabilities: [
      'view_assigned_organizations',
      'view_assigned_brands',
      'view_analytics_for_assigned_resources',
      'manage_account_settings',
      'contact_agent',
      'request_new_brands'
    ],
    restrictions: [
      'cannot_create_organizations',
      'cannot_create_brands',
      'cannot_manage_other_users',
      'limited_to_assigned_resources_only'
    ]
  }
};

export const getUserRole = (user) => {
  if (user.is_super_admin) return USER_ROLES.SUPER_ADMIN;
  if (user.is_admin) return USER_ROLES.ADMIN;
  if (user.is_operator) return USER_ROLES.OPERATOR;
  return USER_ROLES.CLIENT;
};

export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role] || null;
};

export const hasPermission = (user, capability) => {
  const userRole = getUserRole(user);
  const permissions = ROLE_PERMISSIONS[userRole];
  return permissions?.capabilities?.includes(capability) || false;
};

export const canAccessOrganization = (user, organizationId) => {
  const userRole = getUserRole(user);

  // Super admins and admins can access all organizations
  if (userRole === USER_ROLES.SUPER_ADMIN || userRole === USER_ROLES.ADMIN) {
    return true;
  }

  // Operators can access organizations of their assigned clients
  if (userRole === USER_ROLES.OPERATOR) {
    return user.assigned_client_organizations?.includes(organizationId) || false;
  }

  // Clients can only access their assigned organizations
  if (userRole === USER_ROLES.CLIENT) {
    return user.assigned_organizations?.includes(organizationId) || false;
  }

  return false;
};

export const canAccessBrand = (user, brandId, organizationId = null) => {
  const userRole = getUserRole(user);

  // Super admins and admins can access all brands
  if (userRole === USER_ROLES.SUPER_ADMIN || userRole === USER_ROLES.ADMIN) {
    return true;
  }

  // Operators can access brands of their assigned clients
  if (userRole === USER_ROLES.OPERATOR) {
    return user.assigned_client_brands?.includes(brandId) || false;
  }

  // Clients can access brands if:
  // 1. They're directly assigned to the brand, OR
  // 2. They're assigned to the organization that owns the brand
  if (userRole === USER_ROLES.CLIENT) {
    const hasDirectBrandAccess = user.assigned_brands?.includes(brandId) || false;
    const hasOrganizationAccess = organizationId &&
      user.assigned_organizations?.includes(organizationId) || false;

    return hasDirectBrandAccess || hasOrganizationAccess;
  }

  return false;
};

export const getAccessibleOrganizations = (user) => {
  const userRole = getUserRole(user);

  if (userRole === USER_ROLES.SUPER_ADMIN || userRole === USER_ROLES.ADMIN) {
    return 'all'; // Indicates access to all organizations
  }

  if (userRole === USER_ROLES.OPERATOR) {
    return user.assigned_client_organizations || [];
  }

  if (userRole === USER_ROLES.CLIENT) {
    return user.assigned_organizations || [];
  }

  return [];
};

export const getAccessibleBrands = (user) => {
  const userRole = getUserRole(user);

  if (userRole === USER_ROLES.SUPER_ADMIN || userRole === USER_ROLES.ADMIN) {
    return 'all'; // Indicates access to all brands
  }

  if (userRole === USER_ROLES.OPERATOR) {
    return user.assigned_client_brands || [];
  }

  if (userRole === USER_ROLES.CLIENT) {
    // Combine directly assigned brands with brands from assigned organizations
    const directBrands = user.assigned_brands || [];
    const orgBrands = user.organization_brands || []; // Brands from assigned organizations
    return [...new Set([...directBrands, ...orgBrands])]; // Remove duplicates
  }

  return [];
};

export const validateAccess = (user, resource, resourceId, requiredCapability = null) => {
  const userRole = getUserRole(user);
  const permissions = ROLE_PERMISSIONS[userRole];

  if (!permissions) {
    return { allowed: false, reason: 'Invalid user role' };
  }

  // Check if user has required capability
  if (requiredCapability && !permissions.capabilities.includes(requiredCapability)) {
    return { allowed: false, reason: `Missing required capability: ${requiredCapability}` };
  }

  // Check resource-specific access
  if (resource === 'organization') {
    if (!canAccessOrganization(user, resourceId)) {
      return { allowed: false, reason: 'No access to this organization' };
    }
  } else if (resource === 'brand') {
    if (!canAccessBrand(user, resourceId)) {
      return { allowed: false, reason: 'No access to this brand' };
    }
  }

  return { allowed: true };
};

export const getRoleDisplayInfo = (role) => {
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return null;

  return {
    name: permissions.name,
    level: permissions.level,
    capabilities: permissions.capabilities.length,
    restrictions: permissions.restrictions.length
  };
};

export const getAvailableActions = (user) => {
  const userRole = getUserRole(user);
  const permissions = ROLE_PERMISSIONS[userRole];

  if (!permissions) return [];

  const actions = [];

  permissions.capabilities.forEach(capability => {
    switch (capability) {
      case 'view_assigned_organizations':
        actions.push({ id: 'view_organizations', label: 'View Organizations', icon: 'building' });
        break;
      case 'view_assigned_brands':
        actions.push({ id: 'view_brands', label: 'View Brands', icon: 'tag' });
        break;
      case 'manage_clients':
        actions.push({ id: 'manage_clients', label: 'Manage Clients', icon: 'user-add' });
        break;
      case 'create_organizations':
        actions.push({ id: 'create_organization', label: 'Create Organization', icon: 'plus-add' });
        break;
      case 'create_brands':
        actions.push({ id: 'create_brand', label: 'Create Brand', icon: 'tag' });
        break;
      case 'view_analytics':
      case 'view_all_analytics':
      case 'view_analytics_for_assigned_resources':
        actions.push({ id: 'view_analytics', label: 'View Analytics', icon: 'chart-area' });
        break;
      case 'system_administration':
        actions.push({ id: 'system_admin', label: 'System Administration', icon: 'adjust' });
        break;
      case 'manage_account_settings':
        actions.push({ id: 'account_settings', label: 'Account Settings', icon: 'user' });
        break;
      default:
        break;
    }
  });

  return actions;
};